// ══════════════════════════════════════════════
//  自定义售卖 - 三级分类树工具函数
//  分类节点结构：{ id, name, code, children? }
// ══════════════════════════════════════════════

// 固定节点：所有分类 / 未分类
export const ALL_KEY = '__all__';
export const NONE_KEY = '__none__';

// 展平为下拉选项（按层级缩进），便于「归属分类」选择
export const flattenCategoryOptions = (nodes = [], level = 1) => {
    const options = [];
    nodes.forEach((node) => {
        options.push({
            value: node.id,
            label: `${'　'.repeat(level - 1)}${node.name}`,
            level,
        });
        if (node.children && node.children.length) {
            options.push(...flattenCategoryOptions(node.children, level + 1));
        }
    });
    return options;
};

export const findCategory = (nodes = [], id) => {
    for (const node of nodes) {
        if (node.id === id) return node;
        const found = findCategory(node.children || [], id);
        if (found) return found;
    }
    return null;
};

// 收集节点及其所有子孙 id
export const collectCategoryIds = (node) => {
    const ids = [node.id];
    (node.children || []).forEach((child) => ids.push(...collectCategoryIds(child)));
    return ids;
};

// 分类层级（1 起）
export const getCategoryLevel = (nodes = [], id, level = 1) => {
    for (const node of nodes) {
        if (node.id === id) return level;
        const found = getCategoryLevel(node.children || [], id, level + 1);
        if (found) return found;
    }
    return 0;
};

// 从根到该节点的路径（含自身）
export const getCategoryPath = (nodes = [], id, path = []) => {
    for (const node of nodes) {
        const nextPath = [...path, node];
        if (node.id === id) return nextPath;
        const found = getCategoryPath(node.children || [], id, nextPath);
        if (found) return found;
    }
    return null;
};

// 子树自身深度（叶子为 1）
export const getSubtreeDepth = (node) => {
    const children = node.children || [];
    if (!children.length) return 1;
    return 1 + Math.max(...children.map(getSubtreeDepth));
};

export const addCategory = (nodes = [], parentId, newNode) => (
    nodes.map((node) => (node.id === parentId
        ? { ...node, children: [...(node.children || []), newNode] }
        : { ...node, children: addCategory(node.children || [], parentId, newNode) }))
);

export const updateCategory = (nodes = [], id, patch) => (
    nodes.map((node) => (node.id === id
        ? { ...node, ...patch }
        : { ...node, children: updateCategory(node.children || [], id, patch) }))
);

export const removeCategory = (nodes = [], id) => (
    nodes
        .filter((node) => node.id !== id)
        .map((node) => ({ ...node, children: removeCategory(node.children || [], id) }))
);

// 拖拽移动：仅做同级/异级重排，dropPosition=0 表示落到目标节点内部成为子节点
export const moveCategory = (nodes, dragId, dropKey, dropPosition) => {
    const next = JSON.parse(JSON.stringify(nodes));

    const takeOut = (arr, key) => {
        for (let i = 0; i < arr.length; i += 1) {
            if (arr[i].id === key) return arr.splice(i, 1)[0];
            const found = takeOut(arr[i].children || [], key);
            if (found) return found;
        }
        return null;
    };

    const insertAt = (arr, key, obj, position) => {
        for (let i = 0; i < arr.length; i += 1) {
            if (arr[i].id === key) {
                arr.splice(position === 0 ? i : i + 1, 0, obj);
                return true;
            }
            if (insertAt(arr[i].children || [], key, obj, position)) return true;
        }
        return false;
    };

    const insertAsChild = (arr, key, obj) => {
        for (let i = 0; i < arr.length; i += 1) {
            if (arr[i].id === key) {
                arr[i].children = [...(arr[i].children || []), obj];
                return true;
            }
            if (insertAsChild(arr[i].children || [], key, obj)) return true;
        }
        return false;
    };

    const dragObj = takeOut(next, dragId);
    if (!dragObj) return nodes;

    if (dropPosition === 0) {
        const dropLevel = getCategoryLevel(next, dropKey);
        if (dropLevel + getSubtreeDepth(dragObj) > 3) return nodes; // 超过三级，取消
        if (!insertAsChild(next, dropKey, dragObj)) return nodes;
    } else if (!insertAt(next, dropKey, dragObj, dropPosition < 0 ? 0 : 1)) {
        return nodes;
    }
    return next;
};
